import React, { useEffect, useState } from "react";
import { useDesktopsStore } from "@/dentlab/src/store/Desktops";
import { useCentralStore } from "@/dentlab/src/store/Central";
import { LocationTabs } from "./components/location-tabs.component";
import { ConnectedDesktops } from "./components/connected-desktops.component";
import { PrinterRoutingTable } from "./components/printer-routing-table.component";
import { PrintQueue } from "./components/print-queue.component";
import { DesktopDownloadHero } from "./components/desktop-download-hero.component";
import { PrintModeToggle } from "./components/print-mode-toggle.component";
import { Download } from "lucide-react";

const DesktopDownloadLink: React.FC = () => {
	const backendUrl =
		import.meta.env.VITE_PDF_MICROSERVICE_URL || "http://localhost:8400";

	function handleDownload() {
		const ua = navigator.userAgent.toLowerCase();
		const platform = ua.includes("win")
			? "win32"
			: ua.includes("mac")
				? "darwin"
				: "linux";
		window.open(
			`${backendUrl}/desktop-downloads/dentlab-desktop/${platform}`,
			"_blank"
		);
	}

	return (
		<button
			onClick={handleDownload}
			className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition-colors"
			data-testid="desktop-download-link"
			aria-label="Druckservice herunterladen"
		>
			<Download className="h-4 w-4" />
			Druckservice herunterladen
		</button>
	);
};

export const DruckerPage: React.FC = () => {
	const organization = useCentralStore((state) => state.organization);
	const role = useCentralStore((state) => state.role);
	const isAdmin = role === 0 || role === 1;
	const {
		locations,
		fetchLocations,
		fetchPrinterConfigs,
		desktopsWithSystems,
		fetchDesktops,
		fetchPrintJobs,
	} = useDesktopsStore((state) => ({
		locations: state.locations,
		fetchLocations: state.fetchLocations,
		fetchPrinterConfigs: state.fetchPrinterConfigs,
		desktopsWithSystems: state.desktopsWithSystems,
		fetchDesktops: state.fetchDesktops,
		fetchPrintJobs: state.fetchPrintJobs,
	}));

	const [selectedLocationId, setSelectedLocationId] = useState<number | null>(
		null
	);

	useEffect(() => {
		const orgId = organization?.id;
		if (!orgId) return;
		fetchLocations(orgId);
		fetchPrinterConfigs(orgId);
		fetchDesktops(orgId);
		fetchPrintJobs(orgId);

		const interval = setInterval(() => {
			fetchDesktops(orgId);
			fetchPrintJobs(orgId);
		}, 30_000);

		return () => clearInterval(interval);
	}, [organization?.id]);

	const defaultLocationId =
		locations.length > 0
			? (locations.find((l) => l.is_default) ?? locations[0]).id
			: null;
	const effectiveLocationId = selectedLocationId ?? defaultLocationId;
	const selectedLocation = locations.find(
		(l) => l.id === effectiveLocationId
	);

	const hasDesktops = desktopsWithSystems.length > 0;
	const getOrgPrintMode = useDesktopsStore((state) => state.getOrgPrintMode);
	const isDesktopMode = effectiveLocationId
		? getOrgPrintMode(effectiveLocationId) === "desktop"
		: false;

	return (
		<div className="flex flex-col gap-8 p-6 max-w-6xl">
			<div className="flex items-center justify-between">
				<h1 className="text-2xl font-semibold text-gray-900">
					Druckereinstellungen
				</h1>
				{isAdmin && isDesktopMode && <DesktopDownloadLink />}
			</div>

			{isAdmin && isDesktopMode && !hasDesktops && (
				<DesktopDownloadHero />
			)}

			{effectiveLocationId && (
				<PrintModeToggle
					locationId={effectiveLocationId}
					organizationId={organization?.id ?? ""}
				/>
			)}

			{isDesktopMode && (
				<>
					<LocationTabs
						locations={locations}
						selectedLocationId={effectiveLocationId}
						onSelectLocation={setSelectedLocationId}
						organizationId={organization?.id ?? ""}
					/>

					{effectiveLocationId && (
						<>
							<ConnectedDesktops
								desktopsWithSystems={desktopsWithSystems}
							/>

							<PrinterRoutingTable
								locationId={effectiveLocationId}
								locationName={selectedLocation?.name ?? ""}
								organizationId={organization?.id ?? ""}
								desktopsWithSystems={desktopsWithSystems}
							/>
						</>
					)}

					<PrintQueue organizationId={organization?.id ?? ""} />
				</>
			)}
		</div>
	);
};
